/**
 * Provider health for the social lookup router: latency EWMA + circuit breaker (plan §4).
 *
 *   closed ──3 consecutive failures/timeouts──▶ open (60 s) ──▶ half-open (ONE probe)
 *      ▲                                                            │
 *      └────────────────── probe succeeds ──────────────────────────┘   (probe fails → open again)
 *
 * The state machine is pure (explicit `now`) so it can be tested without a clock or Redis.
 * One breaker per provider AND capability (`kicksta:search_users`, `igdirect:profile`, …): a lane
 * that is blocked for profiles can still serve search. Persistence is best-effort: an in-process
 * mirror is always kept, and the shared Redis (`rs:h:{lane}`, the same client the caches use) lets
 * every isolate agree. Any Redis error
 * degrades to the mirror — health may never break the data path.
 *
 * 🔴 Browser+edge-safe: only the shared Upstash client, no node:* imports.
 */
import { redisClient } from '../redis';
import { bounded, fireAndForget, runInBackground, type Capability, type Family, type Provider } from './registry';

export const BREAKER_FAILURE_THRESHOLD = 3;
export const BREAKER_OPEN_MS = 60_000;
export const HALF_OPEN_PENALTY_MS = 2_000;
export const SAME_FAMILY_PENALTY = 2;
const EWMA_ALPHA = 0.2;
const MAX_COOLDOWN_MS = 10 * 60_000;
/** A half-open probe that never reports back frees the slot after this long. */
const PROBE_LEASE_MS = 6_000;
const REDIS_STATE_TTL_S = 24 * 60 * 60;
/** Re-read Redis for a provider at most this often (keeps the miss path to one mget per few seconds). */
const REDIS_REFRESH_MS = 3_000;
/** Health reads from the production region exceeded 400 ms; the in-process mirror covers a timeout. */
const HEALTH_READ_WAIT_MS = 700;

export type BreakerState = 'closed' | 'open' | 'half-open';

export interface HealthState {
	/** Latency EWMA (ms), seeded from the bench. */
	ewma: number;
	/** Observed outcomes (informational). */
	samples: number;
	/** Consecutive failures/timeouts. */
	fails: number;
	state: BreakerState;
	/** When the breaker last opened (ms epoch). */
	openedAt: number;
	/** The single half-open probe is leased until this time (0 = free). */
	probeUntil: number;
	updatedAt: number;
}

export function newHealth(seedLatencyMs: number, now: number): HealthState {
	return { ewma: seedLatencyMs, samples: 0, fails: 0, state: 'closed', openedAt: 0, probeUntil: 0, updatedAt: now };
}

/** The state as a caller should see it at `now`: an open breaker whose 60 s elapsed is half-open. */
export function effectiveState(h: HealthState, now: number): BreakerState {
	if (h.state === 'open' && now - h.openedAt >= BREAKER_OPEN_MS) return 'half-open';
	return h.state;
}

/** Lower is better. Open = Infinity; half-open +2000 ms; a second pick from the leader's family ×2. */
export function score(h: HealthState, family: Family, leaderFamily: Family | null, now: number): number {
	const st = effectiveState(h, now);
	if (st === 'open') return Number.POSITIVE_INFINITY;
	const base = h.ewma + (st === 'half-open' ? HALF_OPEN_PENALTY_MS : 0);
	return leaderFamily && family === leaderFamily ? base * SAME_FAMILY_PENALTY : base;
}

/** Healthy = closed breaker (a half-open provider is only ever the single probe, never a hedge). */
export function isHealthy(h: HealthState, now: number): boolean {
	return effectiveState(h, now) === 'closed';
}

/** Admission: may a request go to this provider now? Leases the half-open probe slot. */
export function admit(h: HealthState, now: number): { go: boolean; health: HealthState } {
	const st = effectiveState(h, now);
	if (st === 'closed') return { go: true, health: h };
	if (st === 'open') return { go: false, health: h };
	// half-open: exactly one probe in flight.
	if (h.probeUntil > now) return { go: false, health: h };
	return { go: true, health: { ...h, state: 'half-open', probeUntil: now + PROBE_LEASE_MS, updatedAt: now } };
}

export function applySuccess(h: HealthState, latencyMs: number, now: number): HealthState {
	const ewma = h.samples === 0 ? (h.ewma * 0.5 + latencyMs * 0.5) : h.ewma * (1 - EWMA_ALPHA) + latencyMs * EWMA_ALPHA;
	return { ...h, ewma, samples: h.samples + 1, fails: 0, state: 'closed', probeUntil: 0, updatedAt: now };
}

/** `cooldownMs` (a Retry-After from a rate-limited lane) opens the breaker for that long instead of 60 s. */
export function applyFailure(h: HealthState, now: number, cooldownMs?: number): HealthState {
	const st = effectiveState(h, now);
	const fails = h.fails + 1;
	if (cooldownMs && cooldownMs > 0) {
		const cooldown = Math.min(cooldownMs, MAX_COOLDOWN_MS);
		return { ...h, fails, samples: h.samples + 1, state: 'open', openedAt: now + cooldown - BREAKER_OPEN_MS, probeUntil: 0, updatedAt: now };
	}
	// A failed half-open probe re-opens at once; a closed breaker opens on the 3rd consecutive failure.
	if (st === 'half-open' || fails >= BREAKER_FAILURE_THRESHOLD) {
		return { ...h, fails, samples: h.samples + 1, state: 'open', openedAt: now, probeUntil: 0, updatedAt: now };
	}
	return { ...h, fails, samples: h.samples + 1, updatedAt: now };
}

// ── Persistence (mirror + shared Redis, best-effort) ────────────────────────────────

const mirror = new Map<string, HealthState>();
const lastRedisRead = new Map<string, number>();
let clock: () => number = () => Date.now();

/** Breaker identity: provider + capability. */
export const laneId = (provider: Provider, capability: Capability): string => `${provider.id}:${capability}`;
const redisKey = (lane: string) => `rs:h:${lane}`;
const probeKey = (lane: string) => `rs:probe:${lane}`;

let redisOn = true;

/** Test hooks: replace the clock, switch Redis off, drop all in-process state. */
export function __setClock(fn: (() => number) | null): void {
	clock = fn ?? (() => Date.now());
}
export function __setRedis(enabled: boolean): void {
	redisOn = enabled;
}
export function __resetHealth(): void {
	mirror.clear();
	lastRedisRead.clear();
}

function current(provider: Provider, capability: Capability, now: number): HealthState {
	const lane = laneId(provider, capability);
	let h = mirror.get(lane);
	if (!h) {
		h = newHealth(provider.seedLatencyMs, now);
		mirror.set(lane, h);
	}
	return h;
}

/** Health snapshot (keyed by provider id) of these providers for one capability (Redis refreshed at most every 3 s per lane). */
export async function loadHealth(list: Provider[], capability: Capability, useRedis = redisOn): Promise<Map<string, HealthState>> {
	const now = clock();
	if (useRedis) {
		const stale = list.filter((p) => now - (lastRedisRead.get(laneId(p, capability)) ?? 0) > REDIS_REFRESH_MS);
		if (stale.length) {
			try {
				const values = (await bounded(redisClient.mget(...stale.map((p) => redisKey(laneId(p, capability)))), HEALTH_READ_WAIT_MS)) as unknown[];
				stale.forEach((p, i) => {
					const lane = laneId(p, capability);
					lastRedisRead.set(lane, now);
					const remote = parseRemote(values[i]);
					const local = mirror.get(lane);
					if (remote && (!local || remote.updatedAt > local.updatedAt)) mirror.set(lane, remote);
				});
			} catch (error: any) {
				console.error('[router/health] redis read failed (using in-process mirror):', error?.message || error);
				for (const p of stale) lastRedisRead.set(laneId(p, capability), now); // do not hammer a down Redis
			}
		}
	}
	const out = new Map<string, HealthState>();
	for (const p of list) out.set(p.id, current(p, capability, now));
	return out;
}

function parseRemote(raw: unknown): HealthState | null {
	try {
		const v: any = typeof raw === 'string' ? JSON.parse(raw) : raw;
		const num = (x: unknown) => typeof x === 'number' && Number.isFinite(x);
		if (v && typeof v === 'object' && num(v.ewma) && num(v.fails) && num(v.samples) && num(v.openedAt) && num(v.updatedAt)
			&& (v.state === 'closed' || v.state === 'open' || v.state === 'half-open')
			&& (v.probeUntil === undefined || num(v.probeUntil))) return { ...v, probeUntil: v.probeUntil ?? 0 } as HealthState;
	} catch { /* corrupt entry: ignore */ }
	return null;
}

function persist(lane: string, h: HealthState): void {
	mirror.set(lane, h);
	if (!redisOn) return;
	fireAndForget('health write', () => redisClient.set(redisKey(lane), JSON.stringify(h), { ex: REDIS_STATE_TTL_S }));
}

/** Reserve a request slot. Closed → go. Open → no. Half-open → the one probe (claimed across isolates
 *  with SET NX, falling back to the in-process lease when Redis is unavailable). */
export async function acquire(provider: Provider, capability: Capability): Promise<boolean> {
	const now = clock();
	const lane = laneId(provider, capability);
	const h = current(provider, capability, now);
	const decision = admit(h, now);
	if (!decision.go) return false;
	if (decision.health !== h) {
		// half-open probe slot taken locally; confirm no other isolate holds it
		if (redisOn) try {
			const claimed = await bounded(redisClient.set(probeKey(lane), '1', { nx: true, px: PROBE_LEASE_MS }));
			if (!claimed) {
				mirror.set(lane, { ...h, state: 'half-open', updatedAt: now }); // keep half-open, probe belongs to someone else
				return false;
			}
		} catch { /* Redis down: the local lease stands */ }
		persist(lane, decision.health);
	}
	return true;
}

export function recordSuccess(provider: Provider, capability: Capability, latencyMs: number): void {
	const now = clock();
	const lane = laneId(provider, capability);
	const before = current(provider, capability, now);
	const wasDegraded = effectiveState(before, now) !== 'closed';
	persist(lane, applySuccess(before, latencyMs, now));
	if (wasDegraded) console.warn(`[router/health] ${lane} breaker CLOSED (probe ok in ${Math.round(latencyMs)} ms)`);
}

// ── breaker alert ─────────────────────────────────────────────────────────────────────
type AlertSink = (title: string, data: Record<string, unknown>) => Promise<unknown>;
/** Default sink: the shared alerts wrapper (../logsnag → notify routing). */
const defaultAlertSink: AlertSink = async (title, data) => {
	const { Notifications } = await import('../logsnag');
	await Notifications.sendNotification('health', title, '🚨', false, null, data);
};
let alertSink: AlertSink = defaultAlertSink;
export function __setAlertSink(sink: AlertSink | null): void {
	alertSink = sink ?? defaultAlertSink;
}

/** One alert per closed→open transition (a cross-isolate SET NX keeps concurrent isolates to one). Never throws. */
function alertBreakerOpen(lane: string, provider: Provider, fails: number, cooldownMs?: number): void {
	runInBackground((async () => {
		try {
			if (redisOn) {
				try {
					const first = await bounded(redisClient.set(`rs:alert:${lane}`, '1', { nx: true, ex: 60 }), 200);
					if (!first) return;
				} catch { /* Redis down: alert anyway */ }
			}
			await alertSink(`Social router: ${provider.id} breaker OPEN (${lane})`, {
				provider: provider.id,
				capability: lane.split(':')[1],
				family: provider.family,
				consecutiveFailures: fails,
				cooldownMs: cooldownMs ?? BREAKER_OPEN_MS,
			});
		} catch (error: any) {
			console.error('[router/health] breaker alert failed (ignored):', error?.message || error);
		}
	})());
}

export function recordFailure(provider: Provider, capability: Capability, cooldownMs?: number): void {
	const now = clock();
	const lane = laneId(provider, capability);
	const before = current(provider, capability, now);
	const beforeState = effectiveState(before, now);
	const wasOpen = beforeState === 'open';
	const after = applyFailure(before, now, cooldownMs);
	persist(lane, after);
	if (after.state === 'open' && beforeState === 'closed') alertBreakerOpen(lane, provider, after.fails, cooldownMs);
	if (after.state === 'open' && !wasOpen) console.warn(`[router/health] ${lane} breaker OPEN (${cooldownMs ? `Retry-After ${Math.round(cooldownMs / 1000)}s` : `${BREAKER_OPEN_MS / 1000}s after ${after.fails} consecutive failures`})`);
}

/** Snapshot of the mirror, for diagnostics. */
export function __snapshot(): Record<string, HealthState> {
	return Object.fromEntries(mirror);
}
